import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Flame } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { privatePageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

export const Route = createFileRoute("/reset-password")({
  head: () =>
    privatePageMetadata(
      "/reset-password",
      "Set a new password | BeHot.Now",
      "Choose a new password for your BeHot.Now account and get back to your boards, boosts and payouts.",
    ),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { t } = useT();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (active) setReady(Boolean(data.session));
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) setReady(true);
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < 8) {
      toast.error(t("shell.resetPassword.tooShort"));
      return;
    }
    if (password !== confirm) {
      toast.error(t("shell.resetPassword.mismatch"));
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success(t("shell.resetPassword.updated"));
      void navigate({ to: "/app" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("shell.resetPassword.updateError"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <div className="text-center">
        <Flame className="mx-auto size-8 text-primary" />
        <h1 className="mt-3 font-display text-2xl font-bold">{t("shell.resetPassword.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {ready
            ? t("shell.resetPassword.readyDesc")
            : t("shell.resetPassword.notReadyDesc")}
        </p>
      </div>

      <form onSubmit={submit} className="card-surface mt-8 space-y-4 p-6">
        <div className="space-y-2">
          <Label htmlFor="password">{t("shell.resetPassword.newPasswordLabel")}</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm">{t("shell.resetPassword.confirmPasswordLabel")}</Label>
          <Input
            id="confirm"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </div>
        <Button type="submit" variant="hot" className="w-full" disabled={busy || !ready}>
          {busy ? t("shell.resetPassword.saving") : t("shell.resetPassword.update")}
        </Button>
      </form>
    </div>
  );
}
